import { extent, linePath, mapLinear, niceTicks } from '../charts/scale';

const WIDTH = 320;
const HEIGHT = 132;
const MARGIN = { left: 44, right: 10, top: 12, bottom: 26 } as const;

/** 折れ線の線種。色だけに頼らず、実線・破線・点線でも区別する（NFR-ACC）。 */
export type LineStyle = 'solid' | 'dashed' | 'dotted';

const DASH: Readonly<Record<LineStyle, string | undefined>> = {
  solid: undefined,
  dashed: '8 4',
  dotted: '2 3',
};

/** グラフ上の、距離の区間への印（G の超過区間など）。 */
export interface ChartBand {
  readonly from: number;
  readonly to: number;
  /** 評価レベル。色に加えて、斜線の模様でも示す */
  readonly level: 'caution' | 'warning' | 'blocking';
}

/** 距離を横軸にした折れ線グラフ（SVG）。再生位置を縦線と点で示す。 */
export function LineChart({
  title,
  xLabel,
  yLabel,
  xs,
  ys,
  markerIndex,
  lineStyle,
  referenceY,
  bands = [],
  marks = [],
  testId,
}: {
  readonly title: string;
  readonly xLabel: string;
  readonly yLabel: string;
  readonly xs: readonly number[];
  readonly ys: readonly number[];
  /** 再生位置に対応する点の番号 */
  readonly markerIndex: number;
  readonly lineStyle: LineStyle;
  /** 基準線の高さ（例：上下 G の 1 G）。範囲内のときだけ描く */
  readonly referenceY?: number;
  /** 距離の区間への印（上限を超えた区間） */
  readonly bands?: readonly ChartBand[];
  /** 距離の地点への印（失速地点） */
  readonly marks?: readonly number[];
  readonly testId: string;
}) {
  const xTicks = niceTicks(extent(xs), 4);
  const yTicks = niceTicks(extent(ys), 3);
  const x = (value: number): number =>
    mapLinear(value, xTicks.domain, MARGIN.left, WIDTH - MARGIN.right);
  const y = (value: number): number =>
    mapLinear(value, yTicks.domain, HEIGHT - MARGIN.bottom, MARGIN.top);
  const points = xs.map((value, i) => [x(value), y(ys[i] ?? 0)] as const);
  const marker = points[Math.min(points.length - 1, Math.max(0, markerIndex))];
  const range = extent(ys);
  const summary = `${title}：最小 ${range.min.toFixed(1)}、最大 ${range.max.toFixed(1)}`;

  return (
    <figure className="chart" data-testid={testId}>
      <figcaption className="chart__title">{title}</figcaption>
      <svg
        className="chart__svg"
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        role="img"
        aria-label={summary}
        preserveAspectRatio="xMidYMid meet"
      >
        {yTicks.ticks.map((tick) => (
          <g key={`y${String(tick)}`}>
            <line
              className="chart__grid"
              x1={MARGIN.left}
              x2={WIDTH - MARGIN.right}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text
              className="chart__tick"
              x={MARGIN.left - 4}
              y={y(tick)}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {tick}
            </text>
          </g>
        ))}
        {xTicks.ticks.map((tick) => (
          <text
            key={`x${String(tick)}`}
            className="chart__tick"
            x={x(tick)}
            y={HEIGHT - MARGIN.bottom + 12}
            textAnchor="middle"
          >
            {tick}
          </text>
        ))}
        <defs>
          <pattern
            id={`${testId}-hatch`}
            width="6"
            height="6"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line x1="0" y1="0" x2="0" y2="6" className="chart__hatch" />
          </pattern>
        </defs>
        {bands.map((band, i) => {
          const x1 = x(band.from);
          const x2 = Math.max(x(band.to), x1 + 2);
          return (
            <rect
              key={`band${String(i)}`}
              className={`chart__band chart__band--${band.level}`}
              x={x1}
              y={MARGIN.top}
              width={x2 - x1}
              height={HEIGHT - MARGIN.bottom - MARGIN.top}
              fill={`url(#${testId}-hatch)`}
              data-testid={`${testId}-band`}
              data-level={band.level}
            />
          );
        })}
        {marks.map((distance, i) => (
          <path
            key={`mark${String(i)}`}
            className="chart__stall"
            d={`M${(x(distance) - 4).toFixed(1)} ${String(MARGIN.top - 2)} L${(x(distance) + 4).toFixed(1)} ${String(MARGIN.top - 2)} L${x(distance).toFixed(1)} ${String(MARGIN.top + 6)} Z`}
            data-testid={`${testId}-stall`}
          />
        ))}
        {referenceY !== undefined &&
          referenceY >= yTicks.domain.min &&
          referenceY <= yTicks.domain.max && (
            <line
              className="chart__reference"
              x1={MARGIN.left}
              x2={WIDTH - MARGIN.right}
              y1={y(referenceY)}
              y2={y(referenceY)}
              data-testid={`${testId}-reference`}
            />
          )}
        <path
          className="chart__line"
          d={linePath(points)}
          strokeDasharray={DASH[lineStyle]}
          data-testid={`${testId}-line`}
        />
        {marker && (
          <g data-testid={`${testId}-marker`} data-x={marker[0].toFixed(1)}>
            <line
              className="chart__marker"
              x1={marker[0]}
              x2={marker[0]}
              y1={MARGIN.top}
              y2={HEIGHT - MARGIN.bottom}
            />
            <circle className="chart__dot" cx={marker[0]} cy={marker[1]} r={3.5} />
          </g>
        )}
        <text className="chart__axis" x={WIDTH - MARGIN.right} y={HEIGHT - 2} textAnchor="end">
          {xLabel}
        </text>
        <text className="chart__axis" x={MARGIN.left - 4} y={8} textAnchor="end">
          {yLabel}
        </text>
      </svg>
    </figure>
  );
}
